import { ClassicEditor } from 'ckeditor5'
import { SuggestionData } from 'ckeditor5-premium-features'
import { ClassicEditorHandler } from 'components/WorkspaceEditor/components/ContentEditor'
import { ChangeDetectionParams, ChangeDetectionResult, detectContentChanges } from 'components/WorkspaceEditor/components/NodeContents/PlainText/plainTextChangeDetection'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import { DataNode } from 'components/WorkspaceEditor/types/types'
import { MutableRefObject, SetStateAction } from 'react'

export interface UseKeyOperationParams {
    /** Reference to the CKEditor instance */
    editorRef: MutableRefObject<ClassicEditor | undefined>
    /** Content item ID to identify the node */
    contentItemId: string | undefined
    /** Blur handler to call when saving content */
    handleOnBlur: ClassicEditorHandler
    /** External state for shouldTriggerNewContent */
    shouldTriggerNewContent: boolean
    /** External setter for shouldTriggerNewContent */
    setShouldTriggerNewContent: (value: boolean) => void

    /** Check if Escape key operation is in progress */
    unFocusEditor: () => void
    /** Set unsaved data state */
    setUnsavedData?: (value: SetStateAction<string>) => void
}

export interface UseKeyOperationReturn {
    /** Execute save-then-add-content operation on Enter key */
    executeSaveAndAddContent: (
        event: React.KeyboardEvent<HTMLElement>,
        changeDetection?: ReturnType<typeof detectContentChanges>
    ) => boolean
    /** Check if Enter key operation is in progress */
    isEnterKeyOperationInProgress: boolean
    /** Handle deactivation of activated editor and content addition for plain text */
    handleDeactivateAndAddContentPlainText: (
        event: React.KeyboardEvent<HTMLElement>,
        changeDetection: ReturnType<typeof detectContentChanges>
    ) => boolean
    /** Handle deactivation of activated editor and content addition for replacement options */
    handleDeactivateAndAddContentReplacementOption: (
        event: React.KeyboardEvent<HTMLElement>,
        hasChanges: boolean
    ) => boolean
    executeSaveOnly: (
        event: React.KeyboardEvent<HTMLElement>,
        changeDetection?: ReturnType<typeof detectContentChanges>
    ) => boolean
    /** Execute reverse operation on Escape key */
    executeEscapeKey: (
        event: React.KeyboardEvent<HTMLElement>,
        textElementText: string,
        changeDetection: ReturnType<typeof detectContentChanges>
    ) => boolean
    /** Execute node navigation on arrow keys */
    executeNodeNavigation: (
        event: React.KeyboardEvent<HTMLElement>,
        treeRef?: {
            current?: {
                scrollTo?: (options: {
                    key: string | number
                    align?: 'top' | 'bottom' | 'auto'
                    offset?: number
                }) => void
            }
        }
    ) => boolean
}

/**
 * Interface for key handler function parameters
 */
export interface KeyHandlerParams {
    /** The keyboard event that triggered the handler */
    event: React.KeyboardEvent<HTMLElement>
    /** Optional text content of the element being edited */
    textElementText?: string
    /** Change detection result for the current content state */
    changeDetection?: {
        hasChanges: boolean
        shouldRevertToPrevious: boolean
        shouldDeactivate: boolean
        shouldAddNewContent: boolean
        shouldExecuteOptimistic: boolean
    }
    /** Optional tree reference for navigation support */
    treeRef?: {
        current?: {
            scrollTo?: (options: {
                key: string | number
                align?: 'top' | 'bottom' | 'auto'
                offset?: number
            }) => void
        }
    }
}

/**
 * Type for key handler functions that return boolean indicating if they handled the event
 */
export type KeyHandler = (params: KeyHandlerParams) => boolean

/**
 * Interface for createKeyHandler parameters
 */
export interface CreateKeyHandlerParams {
    /** List of handler functions to try in order */
    handlers: KeyHandler[]
    /** Function to detect content changes */
    detectContentChanges: (params: ChangeDetectionParams) => ChangeDetectionResult
    /** Current unsaved data */
    unsavedData: string
    /** Original text content */
    originalText: string
    /** Node to add (if any) */
    nodeToAdd?: { id?: string; type?: string } | null
    /** Previous content item view nodes */
    previousContentItemViewNodes?: Partial<ContentItemViewNodesType>[] | null
    /** Initial suggestions in the editor */
    initialSuggestions: SuggestionData[]
    /** Whether whole text was deleted */
    hasDeletedWholeText: boolean
    /** Optional tree reference */
    treeRef?: {
        current?: {
            scrollTo?: (options: {
                key: string | number
                align?: 'top' | 'bottom' | 'auto'
                offset?: number
            }) => void
        }
    }
}

// ============================================================================
// Content Action Types
// ============================================================================

/**
 * Interface for pending operations used in reactive content addition
 * pending operation basically is a temporary content that is not yet added to the map
 * but is being tracked to survive component re-renders
 */
export interface PendingOperation {
    id: string
    originalNodeKey: string
    isAnonymousTemporaryContent: boolean
    timestamp: number
}

/**
 * Represents a change detected in the content item map where content Item map is a Map<string, DataNode> is mapping of content item IDs to their corresponding DataNode objects
 * This is used to track changes in the content item structure, such as additions or updates.
 */
export interface MapChange {
    contentItemId: string
    changeType: 'new' | 'updated'
    node: DataNode
    previousNode?: DataNode
}

/**
 * Props for content addition hooks
 */
export interface useAddContentAfterActionProps {
    /** Flag to determine if new empty content node should be added */
    shouldTriggerNewContent: boolean
    fromHandleKeyDown: boolean
    contentItemId: string | undefined
    setShouldTriggerNewContent: (v: boolean) => void
    setFromHandleKeyDown: (v: boolean) => void
}

// ============================================================================
// Navigation Hook Types
// ============================================================================


/**
 * Parameters for switcher navigation hook
 */
export interface SwitcherNavigationParams {
    /** Current node being navigated from */
    node: DataNode
    /** Array of all tree data */
    treeData: DataNode[]
    /** Array of currently expanded keys */
    expandedKeys: string[]
    /** Array of currently loading article keys */
    articlesLoading: string[]
    /** Tree reference for virtualization support */
    treeRef?: React.MutableRefObject<{
        scrollTo?: (options: {
            key: string | number
            align?: 'top' | 'bottom' | 'auto'
            offset?: number
        }) => void
    }>
}

/**
 * Change types for text element content
 */
export const ChangeType = {
    ADDED: 'ADDED',
    NEW: 'NEW',
    UPDATED: 'UPDATED',
} as const